/**
 * Handling-fee cart line manager.
 *
 * Keeps a single standalone handling-fee line item in the cart in sync with the
 * real (non-fee) items:
 *   - real items present and no fee line  -> add the fee (qty 1)
 *   - no real items but a fee line lingers -> remove the fee
 *   - fee line qty tampered (!= 1)         -> normalize back to 1
 *
 * The fee variant id is injected from Liquid as window.HANDLING_FEE_VARIANT_ID
 * (numeric, or 0 when the shop metafield custom.handling_fee_variant_id is unset
 * -> feature disabled). The checkout UI extension remains the safety net for
 * paths that skip the cart (Buy-it-now, Shop Pay, cart permalinks); both actors
 * add the same variant and only add when absent, so the fee is never doubled.
 */
(function () {
  var FEE_ID = window.HANDLING_FEE_VARIANT_ID;
  var isReconciling = false;
  var smHfCartItems = null;

  // Grab the live <cart-items> element so we can trigger the theme's own refresh
  // (same approach as sm-bulk-add.js). Falls back to a theme:cart:refresh event.
  function smHfWaitCartItems() {
    if (document.querySelector("cart-items")) {
      smHfCartItems = document.querySelector("cart-items");
      return;
    }
    var observer = new MutationObserver(function (mutations, obs) {
      if (document.querySelector("cart-items")) {
        smHfCartItems = document.querySelector("cart-items");
        obs.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function smHfTriggerRefresh() {
    if (smHfCartItems && typeof smHfCartItems.getCart === "function") {
      smHfCartItems.getCart();
    } else {
      document.documentElement.dispatchEvent(
        new CustomEvent("theme:cart:refresh", { bubbles: true })
      );
    }
  }

  function smHfPost(url, body) {
    return fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify(body)
    });
  }

  async function smHfReconcile() {
    if (!FEE_ID) return; // feature disabled (metafield unset -> 0)
    if (isReconciling) return; // our own add/remove triggers another cart:change; ignore it
    isReconciling = true;
    try {
      var cart = await fetch("/cart.js", {
        headers: { Accept: "application/json" }
      }).then(function (r) {
        return r.json();
      });

      var feeLine = null;
      var realCount = 0;
      cart.items.forEach(function (item) {
        if (item.variant_id === FEE_ID) {
          feeLine = item;
        } else {
          realCount += item.quantity;
        }
      });

      if (realCount > 0 && !feeLine) {
        // Case A: real items present, fee missing -> add standalone fee line.
        await smHfPost("/cart/add.js", {
          items: [{ id: FEE_ID, quantity: 1, properties: { _handling_fee: "true" } }]
        });
        smHfTriggerRefresh();
      } else if (realCount === 0 && feeLine) {
        // Case B: last real item removed -> drop the fee line.
        await smHfPost("/cart/change.js", { id: feeLine.key, quantity: 0 });
        smHfTriggerRefresh();
      } else if (feeLine && feeLine.quantity !== 1) {
        // Case C: fee qty tampered -> normalize back to 1.
        await smHfPost("/cart/change.js", { id: feeLine.key, quantity: 1 });
        smHfTriggerRefresh();
      }
      // else: cart already consistent -> no-op (reconciliation loop terminates).
    } catch (err) {
      console.error("Handling-fee reconcile failed:", err);
    } finally {
      isReconciling = false;
    }
  }

  // Reconcile after every cart rebuild (add/remove both dispatch theme:cart:change
  // from CartItems.build()), and once on load for permalink/direct-landing carts.
  document.addEventListener("theme:cart:change", smHfReconcile);
  document.addEventListener("DOMContentLoaded", function () {
    smHfWaitCartItems();
    smHfReconcile();
  });
})();
